// QuickBooks API actions
//
// Endpoints align to the Mongoose `qb` embed contract
// (routes/api/ghl-embed.php `qb` group → QbEmbedController). The backend derives
// the location from the verified SSO context, so the `locationId` query param is
// ignored server-side (kept here only as a harmless hint / for callers).
import axios from 'axios';
import { Api } from 'Utils/api';
import type {
  QuickBooksAuthStatus,
  QuickBooksConfigResponse,
  QuickBooksSaveConfigPayload,
  QuickBooksLogEntry,
  QuickBooksSyncType,
  Pipeline,
} from './types';

// Get headers with GHL SSO token
const getHeaders = (ssoToken: string) => ({
  'X-GHL-SSO': ssoToken,
});

/**
 * Surface the server-provided error message instead of a generic string.
 * QbEmbedController returns `{ error }` for 400/500; Laravel validation returns
 * `{ message, errors }` for 422. Only the user-facing message is shown — no
 * stack/internal details leak.
 */
export const extractQbError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: string; message?: string; errors?: Record<string, string[]> }
      | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first?.[0]) return first[0];
    }
    if (data?.error) return data.error;
    if (data?.message) return data.message;
  }
  return fallback;
};

/**
 * Check QuickBooks authentication status for a location
 */
export const getQuickBooksAuthStatus = async (
  locationId: string,
  ssoToken: string
): Promise<QuickBooksAuthStatus> => {
  const response = await Api.get('/ghl/embed/qb/auth-status', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

/**
 * Get QuickBooks OAuth authorization URL
 */
export const getQuickBooksAuthUrl = async (
  locationId: string,
  ssoToken: string
): Promise<{ authUrl: string }> => {
  const response = await Api.get('/ghl/embed/qb/auth-url', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

/**
 * Disconnect QuickBooks from a location
 */
export const disconnectQuickBooks = async (
  locationId: string,
  ssoToken: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await Api.post(
    '/ghl/embed/qb/disconnect',
    { locationId },
    { headers: getHeaders(ssoToken) }
  );
  return response.data;
};

/**
 * Get QuickBooks configuration for a location.
 * Returns the persisted config (or null) plus the pipelines/items needed to
 * populate the configuration form.
 */
export const getQuickBooksConfig = async (
  locationId: string,
  ssoToken: string
): Promise<QuickBooksConfigResponse> => {
  const response = await Api.get('/ghl/embed/qb/config', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

/**
 * Save QuickBooks configuration
 */
export const saveQuickBooksConfig = async (
  payload: QuickBooksSaveConfigPayload,
  ssoToken: string
): Promise<{ success: boolean }> => {
  const response = await Api.post('/ghl/embed/qb/config', payload, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

/**
 * Get pipelines for configuration dropdown
 */
export const getPipelines = async (
  locationId: string,
  ssoToken: string
): Promise<Pipeline[]> => {
  const response = await Api.get('/ghl/embed/pipelines', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });

  // Transform API response to Pipeline format
  const pipelines = response.data?.data || response.data || [];
  return pipelines.map((p: { id: string; name: string; stages?: { id: string; name: string }[] }) => ({
    id: p.id,
    name: p.name,
    stages: (p.stages || []).map((s: { id: string; name: string }) => ({
      id: s.id,
      name: s.name,
    })),
  }));
};

/**
 * Get QuickBooks activity log
 */
export const getQuickBooksLog = async (
  locationId: string,
  ssoToken: string,
  limit?: number
): Promise<{ logs: QuickBooksLogEntry[] }> => {
  const response = await Api.get('/ghl/embed/qb/activity-log', {
    params: { locationId, limit },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

/**
 * Trigger a manual QuickBooks sync.
 * The backend only supports a bulk entity sync (optional `syncType`); there is
 * no per-opportunity retry endpoint.
 */
export const triggerQuickBooksSync = async (
  locationId: string,
  ssoToken: string,
  syncType?: QuickBooksSyncType
): Promise<{ success: boolean; message?: string }> => {
  const response = await Api.post(
    '/ghl/embed/qb/sync',
    { locationId, syncType },
    { headers: getHeaders(ssoToken) }
  );
  return response.data;
};
